import { Typography, Button, Alert, AlertDescription } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { useState } from "react";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { Control, useFieldArray, useWatch, FieldArrayWithId } from "react-hook-form";
import type {
  SubscriptionFormData,
  SubscriptionV2Variable,
} from "@/pages/subscriptions/services/types";
import type { MetricDefinition } from "@/pages/subscriptions/services/types";
import type { WizardStep } from "../../types";
import { WizardStepEnum, VariableType, ChangeType } from "@/pages/subscriptions/constants";
import VariablePill from "./VariablePill";
import VariableEditorPopover from "./VariableEditorPopover";
import StepCard from "../../CompletedStepCard";

type VariableField = FieldArrayWithId<SubscriptionFormData, "variables", "id">;

interface CustomizeSubscriptionStepProps {
  control: Control<SubscriptionFormData>;
  onNext: () => void;
  onGoToStep: (step: WizardStep) => void;
  categoryDisplayNameLookup?: Record<string, string>;
  metricDisplayNameLookup?: Record<string, string>;
  selectedMetricDefinition?: MetricDefinition | null;
}

const CustomizeSubscriptionStep = ({
  control,
  onNext,
  onGoToStep,
  categoryDisplayNameLookup = {},
  metricDisplayNameLookup = {},
  selectedMetricDefinition = null,
}: CustomizeSubscriptionStepProps) => {
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Use form control to manage variables
  const { fields, update } = useFieldArray({
    control,
    name: "variables",
  });

  // Watch the prompt from form state
  const prompt = useWatch({ control, name: "prompt" }) || "";

  const handleVariableUpdate = (updatedVariable: SubscriptionV2Variable) => {
    // Find the field by variableId (id is overwritten by useFieldArray)
    const index = fields.findIndex(field => field.variableId === updatedVariable.variableId);
    if (index !== -1) {
      const currentField = fields[index];
      // Preserve the useFieldArray-managed 'id' field
      update(index, {
        ...updatedVariable,
        id: currentField.id, // Preserve useFieldArray's internal id
      });

      // If CHANGE_TYPE is being changed to absolute_value, reset THRESHOLD to amount type
      // because percentage threshold is only valid for relative_value comparisons
      if (
        updatedVariable.type === VariableType.CHANGE_TYPE &&
        updatedVariable.value === ChangeType.ABSOLUTE_VALUE
      ) {
        const thresholdIndex = fields.findIndex(field => field.type === VariableType.THRESHOLD);
        if (thresholdIndex !== -1) {
          const thresholdField = fields[thresholdIndex];
          const currentValue = String(thresholdField.value || "");
          // If threshold has a percentage value, reset it to a default amount
          if (currentValue.includes("%")) {
            update(thresholdIndex, {
              ...thresholdField,
              value: "1000",
              displayValue: "1000",
              isPlaceholder: true, // Mark as needing user input
            });
          }
        }
      }
    }
    setOpenPopoverId(null);
    // Clear validation error when user makes an edit
    setValidationError(null);
  };

  // Check if all required variables are filled
  const hasUnfilledVariables = fields.some((field: VariableField) => {
    // Check if variable is a placeholder or has empty value
    return field.isPlaceholder === true || !field.value || field.value === "";
  });

  // Get list of unfilled variable names for error message
  const unfilledVariableNames = fields
    .filter(
      (field: VariableField) => field.isPlaceholder === true || !field.value || field.value === ""
    )
    .map((v: VariableField) => v.displayValue)
    .join(", ");

  const handleNext = () => {
    if (hasUnfilledVariables) {
      setValidationError(`Please select values for: ${unfilledVariableNames}`);
      return;
    }

    // Clear any previous validation error
    setValidationError(null);

    // Data is already in form state, proceed
    onNext();
  };

  const renderPromptParts = () => {
    const parts: JSX.Element[] = [];
    let lastIndex = 0;
    const regex = /\{\{(\w+)\}\}/g;
    let match;

    while ((match = regex.exec(prompt)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} className="al-text-gray-900">
            {prompt.slice(lastIndex, match.index)}
          </span>
        );
      }

      // Add the variable pill
      const variableId = match[1];
      // useFieldArray overwrites 'id', so we use 'variableId' instead
      const fieldIndex = fields.findIndex(field => field.variableId === variableId);
      if (fieldIndex !== -1) {
        const variable = fields[fieldIndex] as SubscriptionV2Variable;
        parts.push(
          <VariableEditorPopover
            key={`var-${variableId}-${fieldIndex}`}
            variable={variable}
            onUpdate={handleVariableUpdate}
            open={openPopoverId === variableId}
            onOpenChange={open => setOpenPopoverId(open ? variableId : null)}
            metricDefinition={selectedMetricDefinition}
            allVariables={fields as SubscriptionV2Variable[]}
          >
            <VariablePill
              variable={variable}
              categoryDisplayNameLookup={categoryDisplayNameLookup}
              metricDisplayNameLookup={metricDisplayNameLookup}
              unit={selectedMetricDefinition?.unit}
            />
          </VariableEditorPopover>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < prompt.length) {
      parts.push(
        <span key={`text-${lastIndex}`} className="al-text-gray-900">
          {prompt.slice(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  return (
    <Stack direction="column" gap="2xl" className="al-p-6">
      {/* Step 1: Subscription Type - Completed */}
      <StepCard
        title="Subscription Type"
        status="completed"
        onEdit={() => onGoToStep(WizardStepEnum.SUBSCRIPTION_TYPE)}
      />
      <Stack direction="column" gap="lg" className="al-p-4 al-rounded-lg al-border">
        {/* Subscription Type - Completed */}

        {/* Customize Your Subscription - Active */}
        <div>
          <Typography variant="body" weight="medium" className="al-text-gray-900 al-mb-4">
            Customize Your Subscription
          </Typography>

          <div className="al-space-y-4">
            <Typography variant="body" className="al-text-sm al-text-gray-600">
              Subscription prompt
            </Typography>

            {/* Prompt editor */}
            <div className="al-border al-border-gray-200 al-rounded-lg al-p-2">
              <Stack direction="row" align="center" justify="between" className="al-mb-4">
                <Typography variant="body" size="xs" className="al-text-gray-500 al-italic">
                  Click on a variable to modify its settings.
                </Typography>
                <Stack direction="row" gap="sm">
                  <Button variant="ghost" size="sm" type="button">
                    <IconRefresh size={16} />
                  </Button>
                </Stack>
              </Stack>

              <div className="al-bg-white al-border-t al-border-gray-200 al-p-4 al-min-h-[400px]">
                <Stack
                  direction="row"
                  gap="sm"
                  wrap
                  align="center"
                  className="al-text-base al-leading-relaxed"
                >
                  {renderPromptParts()}
                </Stack>
              </div>
            </div>
          </div>
        </div>

        {/* Data Refresh Disclaimer */}
        <Alert variant="warning">
          <IconAlertTriangle size={16} />
          <AlertDescription>
            Schedule deliveries after 2:00 PM UTC to ensure you receive the latest data.
          </AlertDescription>
        </Alert>

        {/* Validation Error */}
        {validationError && (
          <div className="al-bg-red-50 al-border al-border-red-200 al-rounded-lg al-p-3">
            <Typography variant="body" className="al-text-red-700 al-text-sm">
              {validationError}
            </Typography>
          </div>
        )}

        {/* Next Button */}
        <Stack direction="row" justify="end" className="al-pt-4">
          <Button
            onClick={handleNext}
            disabled={hasUnfilledVariables}
            className="al-bg-blue-600 hover:al-bg-blue-700"
          >
            Next
          </Button>
        </Stack>
      </Stack>

      {/* Step 3: Destination - Upcoming */}
      <StepCard title="Select your Destination" status="upcoming" />
    </Stack>
  );
};

export default CustomizeSubscriptionStep;
